import { useEffect, useState, type ReactNode } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
} from "@fluentui/react-components";
import { WarningRegular } from "@fluentui/react-icons";
import type { Pill } from "../types";
import { CheckIcon } from "../../shared/components/icons";
import {
  getTurnHighlightPhrases,
  getWordOccurrences,
  surveyWordRegions,
  type PageNumberPosition,
  type WordOccurrences,
} from "../../../integrations/tools/word/services/wordDocument";
import styles from "./WordPillApprovalCard.module.css";

/**
 * Approval card for Word write-pill actions (AIVA-6436).
 *
 * Wraps a turn's ``pill_word_*`` write pills in ONE explicit review surface —
 * the pills were already an implicit approval gate (nothing happens until the
 * user clicks), but the ticket calls for an explicit review UX with a reject
 * affordance and an iterative feedback loop.
 *
 * One card per turn, not per pill: the write pills of a turn carry the same
 * generated content and only differ in WHERE it lands (replace selection /
 * insert at cursor / append), so they render as sibling apply buttons under a
 * single shared preview instead of a stack of near-identical cards.
 *
 * - **Apply buttons** (one per pill, labelled with the pill's own label plus
 *   the affected count where one is known) dispatch the existing office_action
 *   handler unchanged; the first click settles the card.
 * - **Reject** stamps the card locally and states the non-effect (no backend
 *   call — the pending frontend-tool interrupt on the checkpoint will expire on
 *   its 3-day TTL). Always offered: declining is half of what an approval
 *   surface is for, so it does not depend on the parent wiring `onReject`.
 * - **Amend** opens a feedback textarea; on submit the parent fires a new
 *   chat turn with the feedback so the LLM regenerates a fresh pill.
 *
 * Decision is local component state — reload of the taskpane resets it,
 * which matches how the plain pill row behaves today.
 *
 * **Count before consequence.** The card leads with what the write will do and
 * to how many places, because that is the number the user is actually
 * approving. `apply_review` / `apply_styles` carry their count in the pill args;
 * `replace_all` does not, so the card takes a read-only pass over the document
 * on mount (`getWordOccurrences`) and reports "Found 14 matches" before the swap
 * rather than after it. A `replace_all` that matches nothing disables Apply
 * outright — there is nothing to approve.
 *
 * **Then the changes themselves.** A count is not informed consent: "3 matches"
 * doesn't say WHICH three. Under the impact line the card lists each change as
 * struck-through original → highlighted replacement. `apply_review` and
 * `apply_styles` get their rows free from the pill args; `replace_all` gets each
 * match in situ with surrounding sentence context, from the same document read
 * that produced the count. The list caps at `MAX_CHANGE_ROWS` with a "+N more"
 * footer and scrolls, so a 40-finding review can't push the buttons out of the
 * task pane.
 */

type OfficeActionPill = Extract<Pill, { type: "office_action" }>;

type Decision = "approved" | "rejected" | "amended";

interface WordPillApprovalCardProps {
  /** The turn's Word write pills — rendered as sibling apply buttons. */
  readonly pills: readonly OfficeActionPill[];
  readonly onApprove: (pill: OfficeActionPill) => void | Promise<void>;
  readonly onReject?: (pill: OfficeActionPill) => void;
  readonly onAmend?: (pill: OfficeActionPill, feedback: string) => void | Promise<void>;
}

const MAX_PREVIEW_CHARS = 280;

/** Human-friendly title per Word handler. */
function pillTitle(handler: string): string {
  const titles: Record<string, string> = {
    "word.replace_selection": "Replace the selected text",
    "word.insert_at_cursor": "Insert at cursor",
    "word.append_to_end": "Append to document end",
    "word.apply_review": "Apply tracked-change corrections",
    "word.apply_review_edits": "Apply the review as comments and tracked changes",
    "word.apply_styles": "Apply paragraph styles",
    "word.clear_highlights": "Clear session highlights",
    "word.insert_toc": "Insert a table of contents",
    "word.insert_comment": "Insert a comment",
    "word.replace_all": "Find and replace across the document",
    "word.insert_bates": "Apply Bates numbering",
    "word.insert_table": "Insert a table",
    "word.copy_text": "Copy text to clipboard",
    "word.insert_email": "Paste an email into the document",
    "word.insert_page_numbers": "Add page numbers",
  };
  return titles[handler] ?? handler.replace(/^word\./, "").replace(/_/g, " ");
}

/** The inline-formatting markers `sanitise_email_body` (backend) leaves in an
 *  email body — `[[B]]`/`[[I]]`/`[[U]]`/`[[S]]`/`[[H:name]]`/`[[C:#hex]]`/
 *  `[[L:url]]` and their closers, plus the self-contained `[[IMG:cid]]` and
 *  block-level `[[LI:…]]`. The Word inserter turns them into real formatting;
 *  the card must show the words between them, not the syntax. */
const EMAIL_MARKER_RE = /\[\[\/?[A-Z]{1,3}(?::[^\]\s]+)?\]\]/g;

/** Build a compact preview of what will be written for this pill. */
function pillPreview(pill: OfficeActionPill): string | null {
  const args = pill.args ?? {};
  // Text-payload handlers show the text itself.
  if (typeof args.text === "string" && args.text.trim()) {
    return args.text.length > MAX_PREVIEW_CHARS ? `${args.text.slice(0, MAX_PREVIEW_CHARS)}…` : args.text;
  }
  // insert_email carries the payload as `body`, not `text`. Showing it matters
  // more here than anywhere else on this card: the content came from outside the
  // document, so "which email" (the impact line) and "what it actually says"
  // (this preview) are two different questions the user needs answered before
  // someone else's correspondence lands in their file.
  if (typeof args.body === "string" && args.body.trim()) {
    const body = args.body.replace(EMAIL_MARKER_RE, "");
    return body.length > MAX_PREVIEW_CHARS ? `${body.slice(0, MAX_PREVIEW_CHARS)}…` : body;
  }
  // Note there is deliberately no branch for `changes` or `find`/`replace`:
  // batch and find/replace pills are described by `impactLine`, and stating the
  // same count twice reads as two different facts about one write.
  // Comment handler shows phrase + comment.
  if (typeof args.phrase === "string" && typeof args.comment === "string") {
    return `Comment on “${args.phrase}”: ${args.comment}`;
  }
  // Insert table shows header/row counts.
  if (Array.isArray(args.headers) && Array.isArray(args.rows)) {
    return `Insert a table (${args.headers.length} columns, ${args.rows.length} rows).`;
  }
  return null;
}

/** Card heading: the specific action for one pill, generic for a group. */
function cardTitle(pills: readonly OfficeActionPill[]): string {
  return pills.length === 1 && pills[0]
    ? `Review before applying — ${pillTitle(pills[0].handler)}`
    : "Review before applying";
}

/** "1 change" / "2 changes" — `many` is given explicitly where +s is wrong
 *  ("match" → "matches"). */
const plural = (n: number, one: string, many = `${one}s`): string =>
  `${n} ${n === 1 ? one : many}`;

/** The batch list a pill carries, or null if this pill has none. RV-01's review
 *  pill names it `findings` because its rows are not replacement pairs — each
 *  carries the problem it raises, and a suggestion only sometimes. */
function changeList(pill: OfficeActionPill): unknown[] | null {
  const changes = pill.args?.changes ?? pill.args?.findings;
  return Array.isArray(changes) ? changes : null;
}

/** Handlers whose blast radius is `args.changes.length` — known without asking
 *  the document, so the count is available on first render. */
const CHANGE_LIST_IMPACT: Record<string, (n: number) => string> = {
  "word.apply_review": (n) => `This will apply ${plural(n, "change")} across the document.`,
  "word.apply_review_edits": (n) =>
    `This will add ${plural(n, "comment")} and mark the suggested wording as tracked changes, ` +
    "so nothing is rewritten without a revision you can reject.",
  "word.apply_styles": (n) => `This will restyle ${plural(n, "paragraph")}.`,
};

/** The count the Apply button should advertise, or null when unknown. */
function pillCount(pill: OfficeActionPill, matchCount: number | null): number | null {
  if (pill.handler === "word.replace_all") return matchCount;
  if (!CHANGE_LIST_IMPACT[pill.handler]) return null;
  return changeList(pill)?.length ?? null;
}

/** A label that already ends in "(n)". */
const LABEL_CARRIES_COUNT = /\(\d+\)\s*$/;

/**
 * The Apply button's text.
 *
 * The count is appended only when the label does not already carry one. The
 * batch pills mint their own labelled count at emit time ("Apply review (3)",
 * "Apply changes (4)") precisely so the plain pill row can show it, and
 * appending a second copy here rendered "Apply review (3) (3)" — one number
 * read as two different facts about the same write.
 */
function applyLabel(pill: OfficeActionPill, count: number | null): string {
  if (count === null || LABEL_CARRIES_COUNT.test(pill.label)) return pill.label;
  return `${pill.label} (${count})`;
}

/**
 * The consequence sentence — what will change and in how many places, stated
 * before the user can act on it. Null when this pill has no meaningful blast
 * radius to report (the preview alone carries it).
 */
function impactLine(
  pill: OfficeActionPill,
  matchCount: number | null,
  counting: boolean,
): string | null {
  const args = pill.args ?? {};

  // A pill may arrive with its consequence already computed, when only the
  // emitter could know it. Trusted verbatim rather than recomputed: this
  // component has no Office context of its own, and no access to the mailbox.
  //   - Bates and page numbers each survey the section header/footer (a pure
  //     read) so this line can name what is already sitting there before
  //     anything is written past it (DP-02 AC-5 / DP-03, closing G-12).
  //   - insert_email has only the emitter holding the sender and date, and
  //     identifying WHICH message is about to land is the whole point of
  //     carding it (DP-05).
  if (typeof args.impact_line === "string" && args.impact_line.trim()) {
    return args.impact_line;
  }

  if (pill.handler === "word.replace_all") {
    const find = typeof args.find === "string" ? args.find : "";
    const to = typeof args.replace === "string" ? args.replace : "";
    if (counting) return `Counting matches for “${find}”…`;
    if (matchCount === 0) return `No matches for “${find}” — there is nothing to replace.`;
    if (matchCount === null) {
      // Count unavailable (no Office host, or the host refused the search).
      // Say what will happen without inventing a number.
      return `This will replace every occurrence of “${find}” with “${to}” throughout the document.`;
    }
    return `Found ${plural(matchCount, "match", "matches")} for “${find}”. This will replace every one with “${to}”.`;
  }

  const describe = CHANGE_LIST_IMPACT[pill.handler];
  const changes = changeList(pill);
  if (describe && changes) return describe(changes.length);
  return null;
}

/**
 * The group's consequence line — first pill that yields one.
 *
 * Exception: several pills on the SAME handler are alternatives, not one action
 * in several places. Quoting the first candidate's impact line would describe
 * one option while offering several buttons, so the card states the choice
 * instead and lets the per-button labels do the identifying.
 */
function groupImpact(
  pills: readonly OfficeActionPill[],
  matchCount: number | null,
  counting: boolean,
): string | null {
  const first = pills[0];
  if (first && pills.length > 1 && pills.every((p) => p.handler === first.handler)) {
    return `${pills.length} options — choose one. Nothing is inserted until you do.`;
  }
  for (const pill of pills) {
    const line = impactLine(pill, matchCount, counting);
    if (line) return line;
  }
  return null;
}

/** How many change rows to list before collapsing into "+N more". */
const MAX_CHANGE_ROWS = 8;

/** One reviewable row: what the text is now, and what it becomes. */
interface ChangeRow {
  /** Current text, with the affected part optionally isolated for emphasis. */
  fromBefore?: string;
  from: string;
  fromAfter?: string;
  /** Resulting text. Omitted when the row is context-only (find/replace), or
   *  when the finding is comment-only — see `comment`. */
  to?: string;
  /** The note a COMMENT-ONLY finding leaves. Mutually exclusive with `to`: the
   *  text is flagged and left as it is, so the row must not render an arrow into
   *  a replacement that will never happen (AIVA-7722). */
  comment?: string;
  /** A STYLE change (apply_styles): `to` names the style the paragraph takes, and
   *  the text itself is kept exactly — so it must not be struck through as if it
   *  were being deleted (AIVA-8606). */
  restyle?: true;
}

/**
 * A pill's change list, plus the whole-sentence rewrites behind it.
 *
 * `rewrites` is populated only when the rows were split per highlighted phrase:
 * those rows show WHICH WORDS are passive and deliberately carry no `to`, so
 * the replacement they all share is stated once underneath instead of repeated
 * on every row (AIVA-7724).
 */
/** A whole-sentence rewrite, carrying its own stable key (the finding's
 *  matchText) so the rendered list never keys off an array index. */
interface Rewrite {
  key: string;
  text: string;
}

interface ChangePreview {
  rows: ChangeRow[];
  total: number;
  rewrites?: Rewrite[];
}

/** How much of the sentence to keep either side of a marked phrase. Enough to
 *  place the phrase in its clause without reprinting the whole sentence on
 *  every row — five rows of an eighty-word clause is a wall of text. */
const PHRASE_CONTEXT_CHARS = 46;

/** Trim context to `PHRASE_CONTEXT_CHARS`, on a word boundary, with a leading
 *  or trailing ellipsis when anything was cut. */
function trimContext(text: string, side: "before" | "after"): string {
  if (text.length <= PHRASE_CONTEXT_CHARS) return text;
  if (side === "before") {
    const tail = text.slice(-PHRASE_CONTEXT_CHARS);
    const cut = tail.indexOf(" ");
    return `…${cut >= 0 ? tail.slice(cut + 1) : tail}`;
  }
  const head = text.slice(0, PHRASE_CONTEXT_CHARS);
  const cut = head.lastIndexOf(" ");
  return `${cut >= 0 ? head.slice(0, cut) : head}…`;
}

/**
 * Split a sentence into one row per highlighted phrase it contains.
 *
 * Case-insensitive, because the model quotes the phrase it highlighted and
 * `highlightWordText` itself searches case-insensitively — a phrase that
 * starts a sentence comes back capitalised in one and not the other.
 *
 * Only phrases that actually occur are returned, in the order they appear in
 * the SENTENCE rather than the order they were highlighted, so the rows read
 * left to right the way the reader's eye moves. A phrase found more than once
 * is marked at each occurrence: each is its own passive construction, which is
 * what the skill's own rules say.
 */
function markPhrasesInSentence(
  sentence: string,
  phrases: readonly string[],
): Array<{ fromBefore?: string; from: string; fromAfter?: string }> {
  const haystack = sentence.toLowerCase();
  const hits: Array<{ at: number; length: number }> = [];
  for (const phrase of phrases) {
    const needle = phrase.trim().toLowerCase();
    if (!needle) continue;
    for (let at = haystack.indexOf(needle); at >= 0; at = haystack.indexOf(needle, at + needle.length)) {
      // Drop a hit that sits inside one already recorded: the same passive can
      // be highlighted as "shall be provided" and "shall be provided by the
      // Supplier", and marking both would show the reader one construction
      // twice.
      const covered = hits.some((h) => at >= h.at && at + needle.length <= h.at + h.length);
      if (!covered) hits.push({ at, length: needle.length });
    }
  }
  if (hits.length === 0) return [];
  // Widen: a hit that CONTAINS an earlier one replaces it, so the longer span wins.
  const kept = hits.filter((h) => !hits.some((o) => o !== h && h.at >= o.at && h.at + h.length <= o.at + o.length));
  // Sorted as its own statement over a copy — `kept` is not ours to reorder in
  // place, and document order is what the rows are read in.
  const inDocumentOrder = [...kept].sort((a, b) => a.at - b.at);
  return inDocumentOrder
    .map(({ at, length }) => {
      const before = trimContext(sentence.slice(0, at), "before");
      const after = trimContext(sentence.slice(at + length), "after");
      return {
        ...(before ? { fromBefore: before } : {}),
        from: sentence.slice(at, at + length),
        ...(after ? { fromAfter: after } : {}),
      };
    });
}

/** The raw finding shape as it arrives in the pill args — unvalidated. */
interface ReviewEditRow {
  matchText?: unknown;
  problem?: unknown;
  suggestion?: unknown;
}

/**
 * One finding's rows.
 *
 * Split into per-phrase rows ONLY when the finding covers MORE THAN ONE
 * highlighted construction — i.e. it is a whole-sentence finding, which
 * /passive now emits only for a rewrite that restructures the clause. Those
 * phrase rows carry no `to` of their own; the sentence rewrite they share is
 * returned separately and stated once beneath them.
 *
 * The ordinary case is one finding per construction, each with its own
 * suggestion, and those render inline as "phrase → rewrite", pointwise.
 * Splitting them too produced two parallel lists — struck-through phrases under
 * "The changes", green ones under "The rewrites" — leaving the reader to pair
 * them up by counting (AIVA-7724).
 */
function reviewEditRows(
  finding: ReviewEditRow,
  highlighted: readonly string[],
): { rows: ChangeRow[]; rewrite?: string } {
  const from = typeof finding.matchText === "string" ? finding.matchText : "";
  if (!from) return { rows: [] };
  const suggestion = typeof finding.suggestion === "string" ? finding.suggestion : "";
  const problem = typeof finding.problem === "string" ? finding.problem : "";
  // A finding with no suggestion is raised for the reviewer to judge:
  // executeReviewEdits leaves it as a COMMENT plus a highlight and rewrites
  // nothing. Putting the problem in `to` rendered it as struck-through original
  // → green replacement — "is stored → Passive voice. Suggested active rewrite:
  // ..." — which says the document is about to be given that sentence. It
  // carries its own field so the row can say what will actually happen
  // (AIVA-7722).
  const comment = suggestion ? undefined : problem || "Comment only — nothing is rewritten.";

  // The phrases are already known client-side from this turn's
  // word_highlight_text calls, so no new prompt or payload contract is needed:
  // split the sentence around each one and let the card mark it in context,
  // exactly as replace_all does with its document samples.
  const spans = markPhrasesInSentence(from, highlighted);
  if (spans.length <= 1) {
    // One construction, or none matched. Either way the row shows its own pair
    // inline — which is also exactly what /review and the other skills sharing
    // this pill have always rendered.
    return { rows: [{ from, ...(comment !== undefined ? { comment } : { to: suggestion }) }] };
  }

  const rows = spans.map((span, i) => ({
    ...span,
    // The comment belongs on the sentence once, not on every phrase in it.
    ...(comment !== undefined && i === spans.length - 1 ? { comment } : {}),
  }));
  return { rows, ...(suggestion ? { rewrite: suggestion } : {}) };
}

/** Every finding's rows, plus the whole-sentence rewrites behind them. */
function reviewEditPreview(findings: readonly ReviewEditRow[]): ChangePreview {
  const highlighted = getTurnHighlightPhrases();
  const rows: ChangeRow[] = [];
  const rewrites: Rewrite[] = [];
  for (const finding of findings.slice(0, MAX_CHANGE_ROWS)) {
    const built = reviewEditRows(finding, highlighted);
    rows.push(...built.rows);
    // Keyed by the finding's own matchText, which is unique per finding — the
    // rows are rendered from this list, and a React key must not be an index.
    if (built.rewrite) rewrites.push({ key: String(finding.matchText), text: built.rewrite });
  }
  return { rows, total: findings.length, ...(rewrites.length > 0 ? { rewrites } : {}) };
}

/**
 * The reviewable change list — the "what exactly will change" the count alone
 * can't convey.
 *
 * `apply_review` / `apply_styles` carry their pairs in the pill args, so the
 * rows are free. `replace_all` carries only the search term, so the rows come
 * from the document read (`samples`) and each shows the match in situ with the
 * replacement applied.
 */
function changeRows(
  pill: OfficeActionPill,
  occurrences: WordOccurrences | null,
): ChangePreview {
  const args = pill.args ?? {};

  if (pill.handler === "word.replace_all") {
    const to = typeof args.replace === "string" ? args.replace : "";
    const samples = occurrences?.samples ?? [];
    return {
      rows: samples.slice(0, MAX_CHANGE_ROWS).map((s) => ({
        fromBefore: s.before,
        from: s.match,
        fromAfter: s.after,
        to,
      })),
      total: occurrences?.count ?? samples.length,
    };
  }

  const changes = changeList(pill);
  if (!changes) return { rows: [], total: 0 };

  if (pill.handler === "word.apply_review") {
    const pairs = changes as Array<{ original?: unknown; replacement?: unknown }>;
    return {
      rows: pairs.slice(0, MAX_CHANGE_ROWS).flatMap((c) => {
        const from = typeof c?.original === "string" ? c.original : "";
        if (!from) return [];
        return [{ from, to: typeof c?.replacement === "string" ? c.replacement : "" }];
      }),
      total: pairs.length,
    };
  }

  if (pill.handler === "word.apply_review_edits") {
    return reviewEditPreview(changes as ReviewEditRow[]);
  }

  if (pill.handler === "word.apply_styles") {
    const styles = changes as Array<{ text?: unknown; style?: unknown }>;
    return {
      rows: styles.slice(0, MAX_CHANGE_ROWS).flatMap((c) => {
        const from = typeof c?.text === "string" ? c.text : "";
        if (!from) return [];
        return [{ from, to: typeof c?.style === "string" ? c.style : "", restyle: true as const }];
      }),
      total: styles.length,
    };
  }

  return { rows: [], total: 0 };
}

/** The group's change list — first pill that yields rows. */
function groupChangeRows(
  pills: readonly OfficeActionPill[],
  occurrences: WordOccurrences | null,
): ChangePreview {
  for (const pill of pills) {
    const result = changeRows(pill, occurrences);
    if (result.rows.length > 0) return result;
  }
  return { rows: [], total: 0 };
}

/** The shared preview: first pill that yields one (the write pills of a turn
 *  carry the same generated text, so any of them is representative). */
function groupPreview(pills: readonly OfficeActionPill[]): string | null {
  for (const pill of pills) {
    const preview = pillPreview(pill);
    if (preview) return preview;
  }
  return null;
}

function stampClass(decision: Decision): string {
  if (decision === "approved") return styles.approvedStamp!;
  if (decision === "rejected") return styles.rejectedStamp!;
  return styles.amendedStamp!;
}

// The settled line states the outcome, and for a rejection states the
// non-effect too — "Rejected" alone leaves the user guessing whether a partial
// write landed first. Partial-apply reporting (N of M applied) comes from the
// handler's own `message`, which posts as its own chat line.
function stampText(decision: Decision): ReactNode {
  if (decision === "approved") return <>Applied <CheckIcon /></>;
  if (decision === "rejected") return "Rejected — the document is unchanged";
  return "Feedback sent ↻";
}

/**
 * A pill carries `confirm_existing` (set by the Bates / page-number emitters
 * when the header/footer already holds content) to say "confirm before you
 * write over existing furniture" — the ticket's explicit-confirmation ask. Such
 * a pill applies through a popup, not on the first click, so the warning can't
 * be skimmed past. Nothing is cleared either way; the field is appended.
 *
 * The flag is a SNAPSHOT taken when the pill was emitted, which is not when it
 * is clicked — see `stillHasExistingContent` for why that has to be rechecked.
 */
function needsExistingConfirm(pill: OfficeActionPill): boolean {
  return pill.args?.confirm_existing === true;
}

/** Where this pill writes — for the popup title. Footer unless it says header. */
function confirmPosition(pill: OfficeActionPill): PageNumberPosition {
  return pill.args?.position === "header" ? "header" : "footer";
}

/**
 * Is the region STILL occupied, at the moment the user clicks (AIVA-8098)?
 *
 * `confirm_existing` was read when the pill was emitted, and the document stays
 * fully editable while the card sits there waiting. A user who undoes the page
 * numbers they just added — the ticket's repro — empties the footer between the
 * two moments, and the snapshot then warns about content that is no longer
 * there. So the flag is verified against the live document before it is acted
 * on, and a region that has since been emptied applies straight through.
 *
 * Only ever RELAXES the gate: a pill without the flag is not surveyed at all,
 * so this cannot introduce a popup anywhere one did not fire before.
 *
 * `surveyWordRegions` resolves null rather than throwing when the host cannot
 * read the region (no Office host, an API refusal), and null means "cannot
 * tell" — the emitted flag is trusted exactly as it was before, so the warning
 * is never dropped on a guess.
 */
async function stillHasExistingContent(pill: OfficeActionPill): Promise<boolean> {
  try {
    const survey = await surveyWordRegions(confirmPosition(pill));
    if (!survey) return true;
    return survey.occupied.length > 0;
  } catch {
    return true;
  }
}

/** What is being added, for the one-line popup body. */
function confirmThing(pill: OfficeActionPill): string {
  return pill.handler === "word.insert_bates" ? "Bates stamp" : "page number";
}

/** The popup body — deliberately one short sentence. */
function confirmBody(pill: OfficeActionPill): string {
  return `It already has content, which is kept — the ${confirmThing(pill)} is added below it.`;
}

export function WordPillApprovalCard({
  pills,
  onApprove,
  onReject,
  onAmend,
}: Readonly<WordPillApprovalCardProps>) {
  const [decision, setDecision] = useState<Decision | undefined>(undefined);
  const [busy, setBusy] = useState<boolean>(false);
  // The pill awaiting existing-content confirmation, or null when no popup.
  const [pendingConfirm, setPendingConfirm] = useState<OfficeActionPill | null>(null);
  const [amendOpen, setAmendOpen] = useState<boolean>(false);
  const [feedback, setFeedback] = useState<string>("");
  // Document read for replace_all: null = unknown/unavailable.
  const [occurrences, setOccurrences] = useState<WordOccurrences | null>(null);
  const [counting, setCounting] = useState<boolean>(false);
  // G-11: matching is case-insensitive by default (replacing "vendor" also
  // rewrites "Vendor"), with no way to ask for otherwise. This is that way.
  const [matchCase, setMatchCase] = useState<boolean>(false);

  const replaceAllPill = pills.find((p) => p.handler === "word.replace_all");
  const replaceAllFind = replaceAllPill?.args?.find;
  const findTerm = typeof replaceAllFind === "string" ? replaceAllFind : "";

  // Read the matches for the phrase this card would replace — on mount, and
  // again whenever Match case is toggled, so the number on the button always
  // belongs to the search that will actually run. The read is best-effort:
  // getWordOccurrences resolves null rather than throwing when there's no Office
  // host (tests, browser preview), and the impact line falls back to uncounted
  // wording with no change rows.
  useEffect(() => {
    if (!findTerm) return;
    let live = true;
    setCounting(true);
    getWordOccurrences(findTerm, 5, matchCase)
      .then((result) => { if (live) setOccurrences(result); })
      .catch(() => { if (live) setOccurrences(null); })
      .finally(() => { if (live) setCounting(false); });
    return () => { live = false; };
  }, [findTerm, matchCase]);

  const matchCount = occurrences?.count ?? null;
  const firstPill = pills[0];
  const decided = decision !== undefined;
  const disabled = decided || busy;
  const preview = groupPreview(pills);
  const impact = groupImpact(pills, matchCount, counting);
  const { rows, total: rowTotal, rewrites } = groupChangeRows(pills, occurrences);
  const hiddenRows = Math.max(0, rowTotal - rows.length);
  // A counted replace_all with zero hits has nothing to approve. Block Apply
  // rather than letting the user authorise a no-op and wonder what happened.
  const nothingToApply = Boolean(findTerm) && matchCount === 0;

  if (!firstPill) return null;

  /**
   * What actually gets approved for a find/replace.
   *
   * Two things ride along that the emitter could not know, both G-11 closures:
   * the Match-case choice the user made ON this card, and `expected_count` —
   * the count they are looking at. The handler re-counts before writing and
   * aborts if that number has moved, so the swap that runs is the swap that was
   * authorised. Other pills are dispatched exactly as emitted.
   */
  const withApprovalArgs = (pill: OfficeActionPill): OfficeActionPill => {
    if (pill.handler !== "word.replace_all") return pill;
    return {
      ...pill,
      args: {
        ...pill.args,
        match_case: matchCase,
        ...(matchCount !== null ? { expected_count: matchCount } : {}),
      },
    };
  };

  const applyPill = async (pill: OfficeActionPill) => {
    setBusy(true);
    try {
      await onApprove(withApprovalArgs(pill));
      setDecision("approved");
    } finally {
      setBusy(false);
    }
  };

  /**
   * Re-read the region the click is about to write into, then either warn or
   * apply (AIVA-8098).
   *
   * The read is held under `busy` so the button cannot be clicked a second time
   * while the document is being surveyed — without it a double click could
   * dispatch two writes past a gate that had not answered yet.
   */
  const confirmOrApply = async (pill: OfficeActionPill) => {
    setBusy(true);
    let occupied: boolean;
    try {
      occupied = await stillHasExistingContent(pill);
    } finally {
      setBusy(false);
    }
    if (occupied) {
      setPendingConfirm(pill);
      return;
    }
    // Emptied since the pill was emitted: there is nothing to warn about, so
    // the click applies directly rather than raising a popup over a blank
    // region the user has just cleared themselves.
    await applyPill(pill);
  };

  const handleApprove = (pill: OfficeActionPill) => {
    if (disabled) return;
    // Existing header/footer content: confirm through the popup first. Every
    // other pill applies on the click, exactly as before.
    if (needsExistingConfirm(pill)) {
      void confirmOrApply(pill);
      return;
    }
    void applyPill(pill);
  };

  const confirmProceed = () => {
    const pill = pendingConfirm;
    setPendingConfirm(null);
    if (pill) void applyPill(pill);
  };

  const handleReject = () => {
    if (disabled) return;
    onReject?.(firstPill);
    setDecision("rejected");
  };

  const handleAmendSubmit = async () => {
    const trimmed = feedback.trim();
    if (!trimmed || disabled) return;
    setBusy(true);
    try {
      await onAmend?.(firstPill, trimmed);
      setDecision("amended");
      setAmendOpen(false);
      setFeedback("");
    } finally {
      setBusy(false);
    }
  };

  const handleAmendCancel = () => {
    setAmendOpen(false);
    setFeedback("");
  };

  return (
    <section className={styles.card} aria-label="Approval required — review before applying">
      <div className={styles.title}>{cardTitle(pills)}</div>
      {impact && <div className={styles.impact}>{impact}</div>}
      {rows.length > 0 && (
        <div className={styles.changes}>
          <div className={styles.previewLabel}>
            {rows.length === 1 ? "The change" : "The changes"}
          </div>
          <ol className={styles.changeList}>
            {rows.map((row, idx) => (
              <li key={`${row.from}-${idx}`} className={styles.changeRow}>
                <span className={styles.changeFrom}>
                  {row.fromBefore}
                  {/* Struck through only when something replaces it. A comment-only
                      finding, and a restyle, leave the text exactly as it is. */}
                  <mark className={row.comment || row.restyle ? styles.changeMarkKept : styles.changeMark}>
                    {row.from}
                  </mark>
                  {row.fromAfter}
                </span>
                {row.comment !== undefined && (
                  <span className={styles.changeTo}>
                    <span className={styles.changeCommentLabel}>Comment:</span>
                    {row.comment}
                  </span>
                )}
                {row.to !== undefined && (
                  <span className={styles.changeTo}>
                    <span className={styles.changeArrow} aria-hidden="true">→</span>
                    <mark className={styles.changeMarkTo}>{row.to || "(removed)"}</mark>
                  </span>
                )}
              </li>
            ))}
          </ol>
          {hiddenRows > 0 && (
            <div className={styles.changeMore}>
              +{hiddenRows} more not shown
            </div>
          )}
          {/* The replacement the phrase rows above all share. Stated once,
              because every one of those rows is a passive construction inside
              the SAME sentence and repeating the rewrite on each would print
              the same paragraph five times (AIVA-7724). */}
          {rewrites && rewrites.length > 0 && (
            <div className={styles.rewrites}>
              <div className={styles.previewLabel}>
                {rewrites.length === 1 ? "The rewrite" : "The rewrites"}
              </div>
              <ol className={styles.changeList}>
                {rewrites.map((rewrite) => (
                  <li key={rewrite.key} className={styles.changeRow}>
                    <span className={styles.changeTo}>
                      <span className={styles.changeArrow} aria-hidden="true">→</span>
                      <mark className={styles.changeMarkTo}>{rewrite.text}</mark>
                    </span>
                  </li>
                ))}
              </ol>
            </div>
          )}
        </div>
      )}
      {preview && (
        <div className={styles.preview}>
          <div className={styles.previewLabel}>Preview</div>
          {preview}
        </div>
      )}
      {findTerm && !amendOpen && (
        <label className={styles.matchCase}>
          <input
            type="checkbox"
            checked={matchCase}
            disabled={disabled}
            onChange={(e) => setMatchCase(e.target.checked)}
          />
          {" "}
          Match case
        </label>
      )}
      {!amendOpen && (
        <div className={styles.actions}>
          {pills.map((pill, idx) => {
            const count = pillCount(pill, matchCount);
            return (
              <Button
                key={`${pill.handler}-${idx}`}
                appearance={idx === 0 ? "primary" : "secondary"}
                size="small"
                disabled={disabled || counting || nothingToApply}
                title={pillTitle(pill.handler)}
                onClick={() => handleApprove(pill)}
              >
                {applyLabel(pill, count)}
              </Button>
            );
          })}
          <Button appearance="secondary" size="small" disabled={disabled} onClick={handleReject}>
            Reject
          </Button>
          {onAmend && (
            <Button
              appearance="secondary"
              size="small"
              disabled={disabled}
              onClick={() => setAmendOpen(true)}
            >
              Amend
            </Button>
          )}
          {decided && <span className={stampClass(decision!)}>{stampText(decision!)}</span>}
        </div>
      )}
      {amendOpen && (
        <div className={styles.amendPanel}>
          <textarea
            className={styles.amendTextarea}
            placeholder="Describe how you'd like this changed…"
            value={feedback}
            onChange={(e) => setFeedback(e.target.value)}
            rows={3}
            aria-label="Feedback for the assistant"
          />
          <div className={styles.actions}>
            <Button
              appearance="primary"
              size="small"
              disabled={!feedback.trim() || busy}
              onClick={handleAmendSubmit}
            >
              Send feedback
            </Button>
            <Button appearance="secondary" size="small" disabled={busy} onClick={handleAmendCancel}>
              Cancel
            </Button>
          </div>
        </div>
      )}
      <Dialog
        open={pendingConfirm !== null}
        onOpenChange={(_, data) => {
          if (!data.open) setPendingConfirm(null);
        }}
      >
        <DialogSurface className={styles.confirmSurface}>
          <DialogBody>
            <DialogTitle className={styles.confirmTitle}>
              <span className={styles.confirmTitleRow}>
                <WarningRegular className={styles.confirmIcon} aria-hidden="true" />
                {pendingConfirm ? `Add to the ${confirmPosition(pendingConfirm)}?` : "Add to the region?"}
              </span>
            </DialogTitle>
            <DialogContent className={styles.confirmBody}>
              {pendingConfirm ? confirmBody(pendingConfirm) : ""}
            </DialogContent>
            <DialogActions className={styles.confirmActions}>
              <Button appearance="secondary" size="small" onClick={() => setPendingConfirm(null)}>
                Cancel
              </Button>
              <Button appearance="primary" size="small" disabled={busy} onClick={confirmProceed}>
                Add anyway
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </section>
  );
}
